"use client";

import React from "react";
import { Button } from "react-bootstrap";
import Link from "next/link";
import { FaArrowRight } from "react-icons/fa";

interface ManagedServiceProps {
  serviceName: string;
}

const extractYouTubeID = (url: string) => {
  // extract ID from https://www.youtube.com/embed/VIDEO_ID...
  const match = url.match(/embed\/([^?]+)/);
  return match ? match[1] : "";
};

const ManagedVideoSection: React.FC<ManagedServiceProps> = ({ serviceName }) => {
  const serviceContent: Record<
  string,
  { title: string; subtitle: string; points: string[]; videoUrl: string }
> = {
  "Managed AWS Cloud Servers": {
    title: "Elevate Your Digital Excellence with Managed AWS Cloud Servers",
    subtitle: "Experience Peak Performance, Security, and Support in the Cloud",
    points: [
      "✓ Seamless AWS Cloud Management for Your Peace of Mind",
      "✓ Robust Security to Safeguard Your Digital Ventures",
      "✓ 24/7 Expert Support, Your Gateway to Digital Success in the Cloud.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Magento Cloud Servers": {
    title: "Elevate Your E-commerce Empire with Managed Magento Cloud Servers",
    subtitle: "Experience Lightning-Fast Speed, Security, and Support for Your Magento Store",
    points: [
      "✓ Customized Magento Cloud Management for Seamless E-commerce Success",
      "✓ Ironclad Security to Safeguard Your Online Retail Ventures",
      "✓ 24/7 Expert Support, Your Gateway to Dominating the E-commerce Landscape.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed PHP Cloud Servers": {
    title: "Elevate Your Web Projects with Managed PHP Cloud Servers",
    subtitle: "Experience Blazing Speed, Security, and Support for Your PHP Applications",
    points: [
      "✓ Customized PHP Cloud Management for Seamless Web Development Success",
      "✓ Robust Security to Safeguard Your Digital Ventures",
      "✓ 24/7 Expert Support, Your Pathway to Dominating the Digital Landscape.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Drupal Cloud Servers": {
    title: "Elevate Your Web Experiences with Managed Drupal Cloud Servers",
    subtitle: "Experience Blazing Speed, Security, and Support for Your Drupal Projects",
    points: [
      "✓ Customized Drupal Cloud Management for Seamless Web Development Success",
      "✓ Robust Security to Safeguard Your Digital Ventures",
      "✓ 24/7 Expert Support, Your Pathway to Dominating the Digital Landscape.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Digital Ocean Cloud Servers": {
    title: "Elevate Your Digital Presence with Managed Digital Ocean Cloud Servers",
    subtitle: "Experience Seamless Performance, Security, and Support in the Cloud",
    points: [
      "✓ Effortless Cloud Management for Your Peace of Mind",
      "✓ Robust Security to Safeguard Your Digital Ventures",
      "✓ 24/7 Expert Support, Your Pathway to Digital Success in the Cloud.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed WordPress Cloud Servers": {
    title: "Supercharge Your Web Presence with Managed WordPress Cloud Servers",
    subtitle: "Experience Blazing Speed, Security, and Support for Your WordPress Website",
    points: [
      "✓ Tailored WordPress Cloud Management for Effortless Website Success",
      "✓ Robust Security to Safeguard Your Digital Ventures",
      "✓ 24/7 Expert Support, Your Pathway to Dominating the Online Landscape.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Laravel Cloud Servers": {
    title: "Elevate Your Web Applications with Managed Laravel Cloud Servers",
    subtitle: "Experience Lightning-Fast Speed, Security, and Support for Your Laravel Projects",
    points: [
      "✓ Tailored Laravel Cloud Management for Seamless Web Application Success",
      "✓ Ironclad Security to Safeguard Your Digital Ventures",
      "✓ 24/7 Expert Support, Your Pathway to Dominating the Digital Landscape.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Joomla Cloud Servers": {
    title: "Elevate Your Web Presence with Managed Joomla Cloud Servers",
    subtitle: "Experience Lightning-Fast Speed, Security, and Support for Your Joomla Websites",
    points: [
      "✓ Tailored Joomla Cloud Management for Seamless Web Development Success",
      "✓ Ironclad Security to Safeguard Your Digital Ventures",
      "✓ 24/7 Expert Support, Your Pathway to Dominating the Online Landscape.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Prestashop Cloud Servers": {
    title: "Empower Your E-commerce Triumph with Managed Prestashop Cloud Servers",
    subtitle: "Experience Blazing Speed, Security, and Support for Your Prestashop Store",
    points: [
      "✓ Tailored Prestashop Cloud Management for Seamless Online Retail Success",
      "✓ Ironclad Security to Safeguard Your E-commerce Ventures",
      "✓ 24/7 Expert Support, Your Pathway to Dominating the Online Marketplace.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed WooCommerce Cloud Servers": {
    title: "Elevate Your Online Store with Managed WooCommerce Cloud Servers",
    subtitle: "Experience Lightning-Fast Speed, Security, and Support for Your WooCommerce Shop",
    points: [
      "✓ Tailored WooCommerce Cloud Management for Seamless Online Retail Success",
      "✓ Ironclad Security to Safeguard Your E-commerce Ventures",
      "✓ 24/7 Expert Support, Your Pathway to Dominating the Online Marketplace.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Kubernetes Hosting": {
    title: "Elevate Your Business with Temok's Cutting-Edge Managed Kubernetes Hosting",
    subtitle: "Unleash the Power of Kubernetes Without the Hassle - Seamless, Secure, Scalable.",
    points: [
      "✓ Effortless Scalability: Scale Your Applications On-Demand, No Limits.",
      "✓ Robust Security: Enterprise-Grade Security to Safeguard Your Containers.",
      "✓ Expert Management: Focus on Your Business, Let Us Handle the Kubernetes Complexity.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Hyper-V Cloud Hosting": {
    title: "Unleash Peak Performance with Temok's Managed Hyper-V Cloud Hosting",
    subtitle: "Harness the Full Potential of Hyper-V - Unmatched Speed, Security, and Support.",
    points: [
      "✓ Blazing Fast Speed: Accelerate Your Applications with Hyper-V's Lightning-Quick Performance.",
      "✓ Ironclad Security: Fortify Your Cloud Environment with Robust Hyper-V Security Measures.",
      "✓ Expert Support: Your Success, Our Priority - 24/7 Support for Seamless Operations.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed MySQL Cloud Hosting": {
    title: "Elevate Your Database Experience with Temok's Managed MySQL Cloud Hosting",
    subtitle: "Unleash the Power of Data with Our Premium Managed MySQL Cloud Solutions.",
    points: [
      "✓ Turbocharged Performance: Optimize Your Database Operations with Blazing-Fast MySQL Cloud Hosting.",
      "✓ Ironclad Security: Safeguard Your Data Assets with Robust Security Protocols and Regular Backups.",
      "✓ Expert Database Management: Our MySQL Experts Ensure Peak Performance and Seamless Operations.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed PostgreSQL Hosting": {
    title: "Elevate Your Data Experience with Temok's Pinnacle of Managed PostgreSQL Hosting",
    subtitle: "Unleash the Power of PostgreSQL - Unmatched Performance, Unrivaled Support.",
    points: [
      "✓ Peak Performance: Propel Your Database with Temok's Turbocharged PostgreSQL Hosting.",
      "✓ Proactive Management: Experience 24/7 Expert Support and Vigilant Monitoring.",
      "✓ Seamless Scalability: Grow Your Database Effortlessly with Temok's Tailored Solutions.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Redis Hosting": {
    title: "Ignite Your Applications with Temok's Premium Managed Redis Hosting",
    subtitle: "Experience Unmatched Speed and Scalability with Our Expert-Managed Redis Solutions.",
    points: [
      "✓ Turbocharge Performance: Ignite Your Application's Speed and Responsiveness.",
      "✓ Effortless Scalability: Seamlessly Scale Your Operations to Meet Growing Demands.",
      "✓ Expert Monitoring and Optimization: Ensure Peak Redis Performance for Your Applications.",
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Kafka Hosting": {
    title: "Unleash Data Flow Mastery - Temok's Premium Managed Kafka Hosting",
    subtitle: "Seamless Streaming, Unmatched Expertise - Temok's Managed Kafka Hosting Solutions.",
    points: [
      "✓ Data Streaming Prowess: Harness the Power of Kafka with Temok's Expert Management.",
      "✓ Scalable Solutions: Tailored Kafka Hosting for Your Growing Data Demands.",
      "✓ 24/7 Kafka Care: Reliability, Performance, and Support - All in One Managed Package."
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed MongoDB Hosting": {
    title: "Temok Unleashes the True Potential of Your Data with Premium Managed MongoDB Hosting",
    subtitle: "Power Your Applications with the Next-Level MongoDB Hosting Experience - Temok's Unrivaled Expertise at Your Service.",
    points: [
      "✓ Exceptional Performance: Elevate Your Data-Driven Applications with Lightning-Fast MongoDB Hosting.",
      "✓ Proactive Management: 24/7 Expert Support Ensures Your MongoDB Deployment is Always at Its Prime.",
      "✓ Effortless Scaling: Seamlessly Expand Your Database Operations with Temok's Tailored MongoDB Solutions."
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Oracle Hosting": {
    title: "Elevate Your Enterprise with Temok's Cutting-Edge Managed Oracle Hosting",
    subtitle: "Unlock Unparalleled Performance and Reliability with Our Expert Oracle Hosting Solutions.",
    points: [
      "✓ Seamless Oracle Database Management for Peak Efficiency.",
      "✓ Tailored Solutions to Scale with Your Growing Business Needs.",
      "✓ 24/7 Expert Support Ensures Uninterrupted Oracle Performance."
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed MariaDB Hosting": {
    title: "Unleash the Power of MariaDB with Temok's Expert Management",
    subtitle: "Experience Peak Database Performance with Our Managed MariaDB Hosting Solutions.",
    points: [
      "✓ Optimize Database Performance for Unmatched Speed.",
      "✓ Hassle-Free MariaDB Management by Our Experts.",
      "✓ Proactive Security Measures for Data Integrity."
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Opencart Hosting": {
    title: "Power Your Online Store with Precision - Temok's Managed Opencart Hosting",
    subtitle: "Experience Opencart Excellence: Reliable, Scalable, and Expertly Managed.",
    points: [
      "✓ Lightning-Fast Loading: Accelerate Sales with Blazing Opencart Performance.",
      "✓ Effortless Scaling: Seamlessly Expand Your Store as Your Business Grows.",
      "✓ Expert Support, Always Ready: Trust in 24/7 Assistance for Your Opencart Success."
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Odoo Hosting": {
    title: "Powering Your Prosperity - Temok's Ultimate Managed Odoo Hosting",
    subtitle: "Seamless Odoo Performance, Expertly Managed for Your Success.",
    points: [
      "✓ Optimal Odoo Performance: Accelerate Your Business with High-Efficiency Hosting.",
      "✓ Custom Scalability: Tailored Solutions to Match Your Odoo Growth Ambitions.",
      "✓ Proactive Support: Our Experts Ensure Your Odoo Experience is Always Exceptional."
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed ElasticSearch Hosting": {
    title: "Ignite Your Search Experience - Temok's Elite Managed ElasticSearch Hosting",
    subtitle: "Unleash the Power of Search: Precision-Tuned and Expertly Managed.",
    points: [
      "✓ Lightning-Fast Search: Elevate User Experience with High-Speed ElasticSearch.",
      "✓ Robust Security to Safeguard Your Digital Ventures.",
      "✓ Expert Management, Always On: 24/7 Support for Your ElasticSearch Triumph."
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  },
  "Managed Prometheus Hosting": {
    title: "Unlock Prometheus Power - Temok's Expertly Managed Hosting Solutions",
    subtitle: "Elevate Your Monitoring Experience with Temok's Managed Prometheus Hosting.",
    points: [
      "✓ Robust Monitoring Infrastructure: Harness the Full Potential of Prometheus.",
      "✓ Tailored Prometheus Solutions: Customized Hosting to Fit Your Monitoring Needs.",
      "✓ Proactive Management: Trust Temok for Peak Prometheus Performance and Support."
    ],
    videoUrl: "https://www.youtube.com/embed/Xg0-Xf0CoyE?si=Cj9dqQbHC_BS65pU",
  }
  
};

  const content = serviceContent[serviceName] || serviceContent["Managed MySQL Cloud Hosting"];

  const videoId = extractYouTubeID(content.videoUrl);

  const link = process.env.NEXT_PUBLIC_API_BASE_URL + "/register.php";

  return (
    <div className="custMargin">
      <div className="navBg py-5">
        <div className="container ">
          <div className="row align-items-center">
            <div className="col-lg-6 col-md-12 col-12">
              <div className="slogans my-3">
                <h3 className="text-white fw-bold display-5">{content.title}</h3>
                <h4 className="text-white fw-semibold fs-4">{content.subtitle}</h4>

                <ul className="list-unstyled">
                  {content.points.map((point, index) => (
                    <li className="text-white py-2" key={index}>{point}</li>
                  ))}
                </ul>

                <form className="d-flex mt-4">
                  <Link href={link}>
                    <Button className="orangeBtn fw-semibold" size="lg">
                      <span>Create Account</span> <FaArrowRight />
                    </Button>
                  </Link>
                </form>
              </div>
            </div>

            {/* VIDEO SECTION */}
            <div className="col-lg-6 col-md-12 col-12">
              <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=1&modestbranding=1&rel=0`}
                  title="Video"
                  frameBorder="0"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                  }}
                ></iframe>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagedVideoSection;
